import React, { useState } from "react";
import { KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";
import PasswordStrength from "@/components/PasswordStrength";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1";

export default function ForcePasswordChange() {
  const { setUser, logout } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.next !== form.confirm) return toast.error("New passwords don't match");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated — welcome in");
      setUser(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative" style={{ background: "var(--vault-app-bg)" }} data-testid="force-password-change">
      <ThemeToggle className="absolute top-5 right-5" testid="force-change-theme-toggle" />
      <div className="vault-card w-full max-w-sm p-7">
        <BrandLogo size="md" subtitle={null} testid="force-change-brand-logo" />
        <h1 className="font-[Outfit] text-2xl font-semibold mt-6">Set a new password</h1>
        <p className="text-sm text-[color:var(--vault-text-secondary)] mt-1">
          Your vault owner reset your password. Enter the temporary password, then choose your own.
        </p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Temporary password</Label>
            <Input type="password" value={form.current} onChange={set("current")} className={inputCls} data-testid="force-current-password" autoFocus />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">New password</Label>
            <Input type="password" value={form.next} onChange={set("next")} className={inputCls} data-testid="force-new-password" />
            <div className="mt-2"><PasswordStrength value={form.next} /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Confirm new password</Label>
            <Input type="password" value={form.confirm} onChange={set("confirm")} className={inputCls} data-testid="force-confirm-password" />
          </div>
          <Button type="submit" disabled={saving} className="w-full rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="force-change-submit">
            <KeyRound size={16} className="mr-1.5" /> {saving ? "Saving…" : "Save new password"}
          </Button>
          <button type="button" onClick={logout} className="w-full text-sm text-[color:var(--vault-text-tertiary)] hover:text-[color:var(--vault-brand)]" data-testid="force-change-signout">
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
